import { useState } from "react";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} StudentReportItem
 * @property {number} id - ID of the student
 * @property {string} name - Name of the student
 * @property {number} progress_percent - Progress percentage
 * @property {number} avg_score - Average quiz score
 * @property {number} quests_assigned - Number of quests assigned
 * @property {string} lessons_completed - Lessons completed (e.g., "0/0")
 */

/**
 * @typedef {object} StudentListPaginated
 * @property {StudentReportItem[]} data - Array of student report items
 * @property {number} current_page - Current page number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

/**
 * @typedef {object} SubSchoolStudentsData
 * @property {number} avg_overall_progress - Average overall progress
 * @property {number} avg_quiz_score - Average quiz score
 * @property {number} total_quests_assigned - Total quests assigned
 * @property {StudentListPaginated} students - Paginated list of students
 */

/**
 * @typedef {object} MetaType
 * @property {number} current_page - Current page number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

/**
 * Custom hook to fetch sub-school students list by school ID with pagination
 */
export const useGetSubSchoolStudentsList = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { axiosInstance } = useAxios();

    const [studentsData, setStudentsData] = useState(
        /** @type {SubSchoolStudentsData | null | undefined} */(undefined)
    );
    const [metaData, setMetaData] = useState(
        /** @type {MetaType | null | undefined} */(undefined)
    );

    /**
     * Fetch sub-school students by school ID with pagination
     * @async
     * @param {number|string} schoolId - ID of the sub-school
     * @param {number} [page] - Page number for pagination
     * @param {number} [per_page] - Number of items per page
     * @returns {Promise<void>}
     */
    const getSubSchoolStudentsList = async (schoolId, page = 1, per_page = ITEMS_PER_PAGE) => {
        if (!schoolId) {
            setError("School ID is required");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");
        try {
            const query = new URLSearchParams();
            query.append("page", page.toString());
            query.append("per_page", per_page.toString());

            const url = `${TENANT_DOMAIN}/organization/admin/reports/sub-school/${schoolId}/students?${query.toString()}`;
            const response = await axiosInstance.get(url);
            const data = response.data;

            setStudentsData(data);

            setMetaData({
                current_page: data?.students?.current_page,
                per_page: data?.students?.per_page,
                total: data?.students?.total,
                last_page: data?.students?.last_page,
            });
        } catch (err) {
            setStudentsData(null);
            setMetaData(null);
            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    return { loading, error, studentsData, getSubSchoolStudentsList, metaData };
};